import { BroadviewClientError, type SafetyReviewWidget } from "@broadview/api-client";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { PortalDataError, PortalLoadingState } from "@/components/PortalDataState";
import { useSafetyReviewQuery } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CircleDot,
  Database,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";

export const Route = createFileRoute("/recall-safety")({
  head: () => ({
    meta: [
      { title: "Recall & Safety — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Governed, tenant-scoped warranty-signal review with explicit source availability and decision-authority boundaries.",
      },
      { property: "og:title", content: "Recall & Safety — Broadview" },
      {
        property: "og:description",
        content:
          "Governed synthetic warranty, field-action, regulatory-evidence, and campaign-completion scenarios.",
      },
    ],
  }),
  component: RecallSafety,
});

function stateTone(state: SafetyReviewWidget["dataState"]) {
  return state === "UNAVAILABLE"
    ? "border-border bg-muted text-muted-foreground"
    : state === "SYNTHETIC"
      ? "border-warning/30 bg-warning/10 text-warning"
      : "border-success/30 bg-success/10 text-success";
}

function RecallSafety() {
  const { tenantContext } = usePortalRuntime();
  const safetyReview = useSafetyReviewQuery();
  const hasPermission =
    tenantContext.permissions.includes("safety_signal.read") &&
    tenantContext.permissions.includes("metric.read");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <ShieldAlert className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Recall & Safety</div>
              <div className="text-xs text-muted-foreground">
                Governed decision support · {tenantContext.tenantName}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Full dashboard
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-6 px-6 py-8">
        <section
          aria-label="Decision authority"
          className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3"
        >
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>
            <div className="text-sm font-semibold">Human-controlled safety review</div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Broadview presents governed warranty evidence for accountable human review. It does
              not make recall or filing decisions. This prototype includes governed synthetic
              field-action, regulatory-evidence, and campaign-completion scenarios through the same
              tenant API contract intended for future authoritative feeds.
            </p>
          </div>
        </section>

        {!hasPermission ? (
          <PortalDataError
            title="Safety review access is not available"
            error={
              new BroadviewClientError({
                status: 403,
                code: "FORBIDDEN",
                message: "The selected Division must grant safety_signal.read and metric.read.",
              })
            }
            onRetry={() => undefined}
          />
        ) : safetyReview.isPending ? (
          <PortalLoadingState label="Loading governed safety review" />
        ) : safetyReview.isError ? (
          <PortalDataError
            title="Safety review could not be loaded"
            error={safetyReview.error}
            onRetry={() => void safetyReview.refetch()}
          />
        ) : (
          <SafetyReviewContent
            widgets={safetyReview.data.widgets}
            warnings={safetyReview.data.warnings}
          />
        )}
      </main>
    </div>
  );
}

function SafetyReviewContent({
  widgets,
  warnings,
}: {
  widgets: SafetyReviewWidget[];
  warnings: readonly { code: string; message: string; widgetIds?: string[] }[];
}) {
  const categorizedItems = widgets.flatMap((widget) =>
    widget.payload.items.map((item) => ({
      category: widget.payload.category,
      categoryLabel: widget.payload.label,
      item,
    })),
  );

  return (
    <>
      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {widgets.map((widget) => (
          <article key={widget.widgetId} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold">{widget.payload.label}</h2>
                <p className="mt-1 text-xs text-muted-foreground">{widget.payload.description}</p>
              </div>
              <span
                className={
                  "rounded-full border px-2 py-0.5 text-[11px] " + stateTone(widget.dataState)
                }
              >
                {widget.dataState}
              </span>
            </div>
            <div className="mt-4 text-2xl font-semibold tabular-nums">
              {widget.dataState === "UNAVAILABLE" ? "—" : widget.payload.items.length}
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              {widget.quality} quality · {widget.freshness.toLowerCase()} ·{" "}
              {widget.reconciliation.toLowerCase()} reconciliation
            </div>
          </article>
        ))}
      </section>

      {warnings.length > 0 && (
        <section className="rounded-xl border border-warning/30 bg-warning/10 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-warning" /> Governed demonstration notice
          </div>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {warnings.map((warning) => (
              <li key={warning.code + warning.message}>{warning.message}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold">Governed safety review scenarios</h2>
            <p className="text-xs text-muted-foreground">
              Tenant-scoped canonical records; every synthetic scenario is decision support only.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs text-primary">
            <CircleDot className="h-3 w-3" /> Governed API
          </span>
        </div>
        {categorizedItems.length === 0 ? (
          <div className="p-10 text-center">
            <Database className="mx-auto h-7 w-7 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-semibold">No governed safety scenarios available</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              No local or browser fixture was substituted.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 text-left">Category</th>
                  <th className="px-4 py-2.5 text-left">Signal</th>
                  <th className="px-4 py-2.5 text-left">State</th>
                  <th className="px-4 py-2.5 text-left">Component / product</th>
                  <th className="px-4 py-2.5 text-left">Governance</th>
                  <th className="px-4 py-2.5 text-left">Provenance</th>
                </tr>
              </thead>
              <tbody>
                {categorizedItems.map(({ category, categoryLabel, item: signal }) => (
                  <tr key={category + signal.id} className="border-t border-border align-top">
                    <td className="px-4 py-3 text-xs font-medium">{categoryLabel}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{signal.title}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {signal.key} · detected {new Date(signal.detectedAt).toLocaleString()}
                      </div>
                      {signal.problemRecord && (
                        <div className="mt-1 text-[11px] text-primary">
                          {signal.problemRecord.displayId} · {signal.problemRecord.status}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "rounded-full border px-2 py-0.5 text-[11px] " +
                          stateTone(signal.dataState)
                        }
                      >
                        {signal.dataState}
                      </span>
                      <div className="mt-2 text-xs">{signal.status}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{signal.component?.name ?? "Component unavailable"}</div>
                      <div className="mt-1 text-muted-foreground">
                        {signal.product?.name ?? "Product unavailable"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{signal.authorityClass}</div>
                      <div className="mt-1 text-muted-foreground">
                        {signal.freshness} · {signal.quality} · {signal.reconciliation}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{signal.sourceSummary}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {signal.provenance.length} lineage record
                        {signal.provenance.length === 1 ? "" : "s"} · cutoff{" "}
                        {new Date(signal.dataCutoffAt).toLocaleString()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
